import { useCallback, useContext, useState, useEffect } from "react";
import { Box, Button, CircularProgress, Container, Grid, Typography, Divider } from "@mui/material";
import { SuccessAlertContext } from "App";
import { useNavigate } from "react-router";
import { checkAccessTokenExpired, getUserType } from "util/auth";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { HelpOutline } from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import Notify from "config/notify";
import ERROR_MESSAGE from "constants/message";
import { AxiosError } from "axios";
import uploadWeeklyCsv from "../api/uploadWeeklyCsv";
import downloadHelpPdf from "../api/downloadHelpPdf";
import downloadLargeCsvApi from "../api/downloadLargeCsvApi";
import checkUploadStatusApi from "../api/checkUploadStatusApi";
import useLargeCsv from "../hooks/useLargeCsv";
import { localEnv } from "../constants";

const RESULT_STATUS = {
  ERROR: 0,
  SUCCESS: 1,
  PENDING: 2,
} as const;


/**
 * 注文一括操作CSVのUpload画面
 */
export default function LargeCsv() {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [uploadCheckStatus, setUploadCheckStatus] = useState<typeof RESULT_STATUS[keyof typeof RESULT_STATUS]>(RESULT_STATUS.SUCCESS);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [isHeadquatersUser, setIsHeadquartersUser] = useState(false);
  const navigate = useNavigate();

  const { showAlert } = useContext(SuccessAlertContext);

  const checkStatus = useCallback(async () => {
    setIsCheckingStatus(true);
    try {
      const { result } = await checkUploadStatusApi();
      setUploadCheckStatus(result)
    } catch (err) {
      console.error(err);
      setUploadCheckStatus(RESULT_STATUS.SUCCESS); // エラー時は成功扱いにして通常の画面を表示
      console.error("前回のアップロード処理の状態の確認に失敗しました。");
    } finally {
      setIsCheckingStatus(false);
    }
  }, []);

  useEffect(() => {
    if (isHeadquatersUser) {
      checkStatus();
    }
  }, [checkStatus, isHeadquatersUser]);

  const {
      isLoading,
      handleDownload,
      progress,
    } = useLargeCsv(showAlert);
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const selectedFile = acceptedFiles[0];
    if (!selectedFile) return;

    const fileSizeMB = selectedFile.size / 1024 / 1024;
    if (fileSizeMB > 100) {
      setErrorMessage("サイズは100MBまでです。\n正しいファイルを選択してください。");
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setErrorMessage(null);
  }, []);

  const onDropRejected = useCallback(() => {
    setErrorMessage(
      "サポートされているファイル形式はCSVのみです。\n正しいファイルを選択してください。",
    );
    setFile(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: { "text/csv": [] },
    multiple: false,
  });

  const checkValidateCsv = async () => {
    if (file?.size === 0) {
      Notify.setNotification({
        show: true,
        title: "空のファイルエラー",
        message: "空のファイルはアップロードできません。",
        titleColor: "var(--error)",
      });
      return false;
    }
    const arrayBuffer = await file?.arrayBuffer();
    const textDecoder = new TextDecoder("utf-8", { fatal: true });
    let text: string;
    try {
      text = textDecoder.decode(arrayBuffer);
    } catch (err) {
      Notify.setNotification({
        show: true,
        title: "文字化けエラー",
        message: "ファイルの文字コードがUTF-8ではないため、アップロードできません。",
        titleColor: "var(--error)",
      });
      return false;
    }
    const mojibakeLikePatterns = [
      "�", // 文字化けの代表的な文字
    ];
    const foundMojibake = mojibakeLikePatterns.some((pattern) => text.includes(pattern));
    if (foundMojibake) {
      Notify.setNotification({
        show: true,
        title: "文字化けエラー",
        message: "ファイルに文字化けのような文字が含まれています。UTF-8になっているか確認してください。",
        titleColor: "var(--error)",
      });
      return false;
    }
    return true;
  }

  const handleUpload = async () => {
    if (!file) {
      showAlert("CSVファイルを選択してください", "error");
      return;
    }
    // ファイルがutf-8であるか、内容がCSV形式になっているかなどのバリデーション
    if (!(await checkValidateCsv())) {
      return;
    }
    setIsUploading(true);
    try {
      const params = {
        csv_file: file,
      };
      await uploadWeeklyCsv(params);
      showAlert("アップロードが完了しました", "success");
      setFile(null);
      setErrorMessage(null);
    } catch (err) {
      console.error(err);
      const error = err as AxiosError;
      // レスポンス自体がなくエラーになった時
      if (!error.response) {
        Notify.setNotification({
          show: true,
          title: "APIに接続できませんでした。",
          confirmLabel: "OK",
          actionsPosition: "end",
        });
        return;
      }
      const { status } = error.response;
      // const data = error.response.data as {code: string, message: string};
      switch (status) {
        case 413:
          Notify.setNotification({
            show: true,
            title: "ファイルサイズ上限エラー",
            message: "該当ファイルのサイズが大きすぎるため、サイズを小さくして再度アップロードしてください。",
            titleColor: "var(--error)",
          });
          break;
        default:
          break;
      }
      showAlert("アップロード処理に失敗しました。", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleBack = () => {
    // 戻る処理をここに実装（例：history.back()など）
    navigate(-1);
  };

  // const formatFileDate = (timestamp: number) => {
  //   const date = new Date(timestamp);
  //   const year = date.getFullYear();
  //   const month = String(date.getMonth() + 1).padStart(2, '0');
  //   const day = String(date.getDate()).padStart(2, '0');
  //   const hours = String(date.getHours()).padStart(2, '0');
  //   const minutes = String(date.getMinutes()).padStart(2, '0');
  //   return `${year}年${month}月${day}日 ${hours}:${minutes}`;
  // };

  const handleFormatDownload = async () => {
    setIsDownloading(true);
    try {
      await downloadLargeCsvApi();
      showAlert("フォーマットをダウンロードしました", "success");
    } catch (err) {
      console.error(err);
      showAlert("フォーマットダウンロード処理に失敗しました。", "error");
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      (async () => {
        const userType = localStorage.getItem("user_type");
        if (!localEnv && !(userType === "C0001" || userType === "C0005")) {
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: "本部ユーザーでログインしてください。再度ログインしてください。",
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
          setIsCheckingStatus(false);
        } else if (!localEnv && checkAccessTokenExpired()) {
          // 期限切れなら
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: ERROR_MESSAGE.UNAUTHORIZED.MESSAGE,
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
          setIsCheckingStatus(false);
        } else {
          setIsHeadquartersUser(true);
        }
      })();
    }, 0);
  }, []);

  return (
    <Box position="relative">
      <Box>
        <Box width="100%" height={32} textAlign="right" position="absolute" top={0} zIndex={1}>
          <Button variant="custom-outlined" onClick={async (e) => {
            const url = await downloadHelpPdf(`${getUserType()}/resources/orders/manual_weekly_restock.pdf`);
            window.open(url, "_blank", "noopener,noreferrer");
          }} startIcon={<HelpOutline color="secondary" />} sx={{height: 24, padding: 1, marginTop: 0.5, marginRight: 1}}>ヘルプ</Button>
        </Box>
        <Box component="header" sx={{ position: "relative", display: "flex", alignItems: "center", px: 2, py: 4 }}>
          <Box sx={{
            position: "absolute",
            left: 60,
            color: "#000",
            textTransform: "none",
          }}>
            <Button onClick={() => handleBack()} variant="custom-outlined">
              <Box>＜ 戻る</Box>
            </Button>
          </Box>
          <Grid container display="flex" alignItems="center" justifyContent="center" sx={{ width: "100%" }}>
            <DownloadIcon sx={{ fontSize: "2rem", color: "text.secondary", mr: 2 }} />
            <Typography
              variant="h5"
              component="h1"
              color="text.secondary"
              sx={{
                textAlign: "center",
                m1: 2,
                fontWeight: 700,
                fontSize: "1.8rem",
              }}
            >
              週次補充CSV
            </Typography>
          </Grid>
        </Box>
        <Container maxWidth="xl">
          {isCheckingStatus ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 130px)">
              {/* <CircularProgress /> */}
            </Box>
          ) : uploadCheckStatus === RESULT_STATUS.ERROR ? (
            // エラー時：真ん中に表示
            <Grid container spacing={3} sx={{ minHeight: "calc(100vh - 130px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 4, borderRadius: 2, boxShadow: 1, display: "flex", flexDirection: "column", mt: "-130px" }}>
                  <Typography variant="subtitle1" color="text.secondary" fontWeight={700} gutterBottom>
                    週次補充CSVのエラーレポートをダウンロードします。
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    前回の週次補充でエラーがあったため、エラーレポートをダウンロードしてください。
                  </Typography>
                  <Box mt={2} textAlign="right">
                    <Button
                      variant="contained"
                      disabled={isLoading}
                      startIcon={<DownloadIcon />}
                      onClick={async () => {
                        await handleDownload();
                        setUploadCheckStatus(RESULT_STATUS.SUCCESS); // ダウンロード後は通常のアップロード画面に遷移
                      }}
                      sx={{
                        bgcolor: "#ffa500",
                        color: "#000000",
                        ":hover": { bgcolor: "#e07b00" },
                        textTransform: "none",
                      }}
                    >
                      {isLoading ? "ダウンロード中" : "エラーレポートをダウンロード"}
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          ) : uploadCheckStatus === RESULT_STATUS.PENDING ? (
            // 処理中：結果を更新ボタンと無視してアップロードボタン
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} sx={{ mx: "auto" }}>
                <Box sx={{ p: 4, borderRadius: 2, boxShadow: 1, height: "calc(100% - 64px)", display: "flex", flexDirection: "column" }}>
                  <Typography variant="subtitle1" color="text.secondary" fontWeight={700} gutterBottom>
                    前回の週次補充がまだ処理中です
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    前回のアップロード処理がまだ完了していません。少々お待ちください。
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                    ・「結果を更新」を選択すると、最新の処理状況を確認します。<br />
                    ・「無視してアップロード」を選択すると、新しいCSVをアップロードできます。
                  </Typography>
                  <Box mt={2} display="flex" gap={2} justifyContent="flex-end">
                    <Button
                      variant="custom-outlined"
                      onClick={() => {
                        setUploadCheckStatus(RESULT_STATUS.SUCCESS);
                      }}
                    >
                      無視してアップロード
                    </Button>
                    <Button
                      variant="contained"
                      disabled={isCheckingStatus}
                      onClick={checkStatus}
                    >
                      {isCheckingStatus ? "確認中..." : "結果を更新"}
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          ) : uploadCheckStatus === RESULT_STATUS.SUCCESS ? (
            // 成功時：真ん中に表示
            <Grid container spacing={0} sx={{ minHeight: "calc(100vh - 160px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 4, borderRadius: 2, boxShadow: 1, display: "flex", flexDirection: "column" }}>
                  <Typography variant="subtitle1" color="text.secondary" fontWeight={700} gutterBottom>
                    週次補充CSVから注文します。
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ダウンロードした週次補充CSVを修正してアップロードし、注文処理を実行します。
                  </Typography>
                  <Box mt={1} textAlign="left">
                    <Button
                      variant="contained"
                      disabled={isDownloading}
                      startIcon={<DownloadIcon />}
                      onClick={handleFormatDownload}
                      sx={{
                        bgcolor: "#ffa500",
                        color: "#000000",
                        ":hover": { bgcolor: "#e07b00" },
                        textTransform: "none",
                        mt: 0,
                      }}
                    >
                      {isDownloading ? "ダウンロード中" : "フォーマットをダウンロード"}
                    </Button>
                  </Box>
                  <Divider sx={{ mt: 3 }} />
                  <Box
                    {...getRootProps()}
                    sx={{
                      position: "relative",
                      width: "auto",
                      minHeight: 180,
                      border: "2px dashed var(--silver-500)",
                      borderRadius: 2,
                      textAlign: "center",
                      cursor: "pointer",
                      p: 3,
                      mt: 3,
                      bgcolor: isDragActive ? "rgba(255,165,0,0.08)" : "transparent",
                      transition: "background-color 0.2s ease",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <input {...getInputProps()} data-testid="weekly-file-input" />
                    <Typography fontWeight={600} gutterBottom>
                      {file ? file.name : "ファイルを選択またはドラッグ&ドロップ"}
                    </Typography>
                    {/* {file && (
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                        更新日時: {formatFileDate(file.lastModified)}
                      </Typography>
                    )} */}
                    <Typography variant="caption" color="text.secondary">
                      対応形式：CSV／最大100MB
                    </Typography>
                    {errorMessage && (
                      <Typography mt={1} variant="body2" color="error" sx={{ whiteSpace: "pre-line" }}>
                        {errorMessage}
                      </Typography>
                    )}
                  </Box>
                  <Box mt={1} textAlign="right">
                    <Button
                      variant="contained"
                      disabled={isUploading || !file}
                      startIcon={<UploadFileIcon />}
                      onClick={handleUpload}
                      sx={{
                        bgcolor: "#ffa500",
                        color: "#000000",
                        ":hover": { bgcolor: "#e07b00" },
                        textTransform: "none",
                        mt: 1,
                      }}
                    >
                      {isUploading ? "アップロード中" : "アップロード"}
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          ) : null}
        </Container>
      </Box>
      {(isUploading || isDownloading || isCheckingStatus) && ( // マスク
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.2)",
            zIndex: (theme) => theme.zIndex.modal + 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {isLoading && ( // プログレスバー&マスク
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.2)",
            zIndex: (theme) => theme.zIndex.modal + 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box sx={{
            width: "80%",
            maxWidth: 400,
            bgcolor: "#fff",
            borderRadius: 2,
            p: 3,
            boxShadow: 3,
            textAlign: "center"
          }}>
            <Typography variant="h6" gutterBottom>
              {progress === 100 ? "ダウンロード完了" : "ダウンロード中... "}{progress !== null ? `${progress}%` : ""}
            </Typography>
            <Box sx={{ width: "100%", bgcolor: "#e0e0e0", borderRadius: 1 }}>
              <Box
                sx={{
                  width: `${progress ?? 0}%`,
                  height: 10,
                  bgcolor: "#66ff00",
                  borderRadius: 1,
                  transition: "width 0.3s ease",
                }}
              />
            </Box>
            {progress === 100 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: "right" }}>
                数秒後に週次補充カードへ遷移します...
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
